import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Switch } from '@mui/material';
import { ThrillIcon, TingleSparkleMarkIcon } from '@repo/ui/icons';
import { checkIsLogin, getServiceName } from '@repo/utils';

import { useGetEnableNsfw, useGetMyProfile, useUpdateNsfwDisable, useUpdateNsfwEnable } from 'services/hooks/user';
import { openModal } from 'store/modalStackStore';

import MinorRestrictionModalBody from 'components/modal/MinorRestrictionModalBody';
import { OutletChildType } from 'components/provider/GlobalProvider';
type Props = {
  handleClick?: () => void;
};

const AdultToggleButton = ({ handleClick }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { device } = useOutletContext<OutletChildType>();
  const { data: userProfile } = useGetMyProfile();

  const isLogin = checkIsLogin();

  const { data: enableNsfwData } = useGetEnableNsfw({ enabled: !!isLogin });
  const { mutateAsync: updateNsfwEnable, isPending: nsfwEnableLoading } = useUpdateNsfwEnable(userProfile?.id);
  const { mutateAsync: updateNsfwDisable, isPending: nsfwDisableLoading } = useUpdateNsfwDisable(userProfile?.id);

  const isAdult = enableNsfwData?.isAdult && !!enableNsfwData?.adultVerifiedAt && !!userProfile?.identityVerifiedAt; // 성인 여부 판단
  const enableNsfw = enableNsfwData?.enableNsfw; // 성인인증 여부 판단

  const handleChangeAdult = async () => {
    // 1. 미성년자의 경우
    if (!isAdult) {
      openModal(<MinorRestrictionModalBody />, {
        modalWrapperStyle: {
          padding: 0,
          width: !device.isMobile ? '100%' : '400px',
          maxWidth: !device.isMobile ? '400' : 'initial',
          borderRadius: !device.isMobile ? '0px' : '12px',
        },
      });
      return;
    }

    // 2. 성인인증이 완료된 유저
    if (enableNsfw) {
      await updateNsfwDisable();
    } else {
      await updateNsfwEnable();
    }
  };

  /*
   * 팅글은 토글 마크를 **신규 브랜드 스파클**로 쓴다(2026-08-19 요청 — 리브랜딩 후에도 구 4각 별이 남아 있었다).
   * 공용 ThrillIcon을 고치면 챗까지 바뀌므로 여기서만 갈아끼운다. 버블챗은 기존 짜릿 아이콘 그대로.
   */
  const isTingle = getServiceName() === 'tingle';
  const thumbIconStyle = {
    boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.06), 0px 3px 8px 0px rgba(0, 0, 0, 0.15)',
  };
  const thumbIcon = isTingle ? <TingleSparkleMarkIcon style={thumbIconStyle} /> : <ThrillIcon style={thumbIconStyle} />;

  return (
    <SLabel>
      {device.isLaptop ? <span>{t('chat.home_nsfw_toggle')}</span> : <span>{t('chat.home_nsfw_toggle_mobile')}</span>}
      <SThrillSwitchButton
        checked={enableNsfw}
        icon={thumbIcon}
        checkedIcon={thumbIcon}
        onChange={handleChangeAdult}
        disableRipple
        disabled={nsfwEnableLoading || nsfwDisableLoading}
        sx={{
          overflow: 'initial',
          '& .MuiSwitch-track': {
            background: theme.color.buttons.default,
          },
        }}
      />
    </SLabel>
  );
};

const SLabel = styled.label`
  display: flex;
  gap: 4px;
  align-items: center;
  cursor: pointer;

  & > span {
    color: ${({ theme }) => theme.color.static.adult};
    z-index: 1;
    ${({ theme }) => theme.font.body4_sb};
    cursor: pointer;
  }
`;

const SThrillSwitchButton = styled(Switch)(({ theme }) => ({
  width: '40px !important',
  padding: '12px 0px !important',
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translate(-1px, 6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translate(15px, 6px)',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.color.static.adult,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: '#D8D8D8',
    borderRadius: 20 / 2,
  },
}));

export default AdultToggleButton;
